import type { Manifest } from '@clawharbor/manifest';
import { bestForeground, contrastRatio } from '@clawharbor/render-core';
import { html, SP } from '../html.js';
import type { VNode } from '../html.js';
import { wordmarkText } from '../tenant-theme.js';

/**
 * The masthead: the crawled site's own palette, full bleed, at the top of the
 * result screen.
 *
 * Up to this point the console has been a dark instrument reading someone
 * else's site. This band is the moment the reading turns into a result — the
 * ground, the text colour, the accent rule, the substituted face and the corner
 * radius are all the tenant's, emitted by `tokensToCss`, the same function that
 * paints the hosted WebMCP page and the MCP Apps widget. If this band looks
 * right, both surfaces look right, because it is one code path.
 *
 * Two rules keep it from becoming a skin rather than a specimen:
 *
 *   1. It ends. Everything below it — the capability descriptions and the
 *      publish gate, the things an operator must READ to decide — stays on the
 *      console's own ground, where the contrast is ours and known.
 *   2. It measures itself. The tenant's own fg/bg pair is scored and the number
 *      is printed. Where the pair fails WCAG AA, `contrastSafeOverrides` has
 *      already re-pointed the foreground and the band says so, in the band.
 */
export function BrandFrame(props: {
  manifest: Manifest;
  cleared: number;
  turnedBack: number;
}): VNode {
  const m = props.manifest;
  const palette = m.brand.palette as Record<string, { value?: string; confidence?: number } | undefined>;
  const canvas = palette['bg.canvas']?.value;
  const ink = palette['fg.default']?.value;
  const primary = palette['brand.primary']?.value;
  const primaryConf = palette['brand.primary']?.confidence ?? 0;

  const ratio = contrastRatio(ink, canvas);
  const corrected = ratio != null && ratio < 4.5;
  // The value contrastSafeOverrides actually substituted, measured — so the
  // band can name the ratio it is really being read at rather than only the one
  // it refused to ship.
  const fixedRatio = corrected ? contrastRatio(bestForeground(canvas), canvas) : null;

  return html`
    <div class="brandframe tenant">
      <div class="wrap brandframe__in">
        <div class="brandframe__mark">
          <h1 class="brandframe__wordmark" tabindex="-1" data-focus-target>${wordmarkText(m)}</h1>
          <span class="brandframe__origin">${m.source.canonicalOrigin}</span>
        </div>

        <div class="brandframe__rule" aria-hidden="true"></div>

        <dl class="brandframe__facts">
          <div class="fact">
            <dt>Cleared</dt>
            <dd>${props.cleared} ${props.cleared === 1 ? 'tool' : 'tools'}</dd>
          </div>
          <div class="fact">
            <dt>Turned back</dt>
            <dd>${props.turnedBack}</dd>
          </div>
          <div class="fact">
            <dt>Primary</dt>
            <dd><i class="fact__dot" style=${`background:${primary ?? 'transparent'}`}></i>${primary ?? '—'}${SP}
              <small>conf ${primaryConf.toFixed(2)}</small></dd>
          </div>
          <div class="fact">
            <dt>Manifest</dt>
            <dd>v${m.manifestVersion}${SP}<small>${m.id}</small></dd>
          </div>
        </dl>

        <p class="brandframe__note">
          Painted with the palette and type this crawl just extracted, by the same emitter that
          paints both published surfaces.${SP}
          ${ratio == null
            ? 'This pair could not be measured.'
            : corrected
              ? html`The site's own text-on-ground pair measures <b>${ratio}:1</b>, below the 4.5:1
                  floor, so the foreground here was re-pointed and now reads at${SP}
                  <b>${fixedRatio}:1</b>. The extracted value is kept in the manifest and shown
                  below, unaltered.`
              : html`Its text-on-ground pair measures <b>${ratio}:1</b>, above the 4.5:1 floor —
                  nothing has been corrected.`}
        </p>
      </div>
    </div>
  `;
}
